/**
 * Quick Actions Bar
 *
 * Floating toolbar for rapid node insertion in workflow builder.
 * Appears when user clicks empty canvas space or presses keyboard shortcut.
 *
 * Features:
 * - One-click common node insertion
 * - Contextually sorted (most-used first)
 * - Keyboard shortcut support
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  FileText,
  Camera,
  Shield,
  GitBranch,
  CheckCircle2,
  User,
  MapPin,
  Clock,
  ChevronDown,
  X,
  Command,
} from 'lucide-react';

// Common node types for quick access
const QUICK_ACTIONS = [
  {
    id: 'id_document',
    label: 'ID Doc',
    icon: FileText,
    nodeType: 'step',
    componentId: 'id_document',
    description: 'Identity document verification',
  },
  {
    id: 'selfie',
    label: 'Selfie',
    icon: Camera,
    nodeType: 'step',
    componentId: 'selfie',
    description: 'Selfie capture for face matching',
  },
  {
    id: 'sanctions',
    label: 'Screening',
    icon: Shield,
    nodeType: 'step',
    componentId: 'sanctions',
    description: 'Sanctions & PEP screening',
  },
  {
    id: 'condition',
    label: 'Condition',
    icon: GitBranch,
    nodeType: 'condition',
    componentId: 'condition',
    description: 'Add conditional branching',
  },
  {
    id: 'end_approved',
    label: 'Approve',
    icon: CheckCircle2,
    nodeType: 'end',
    componentId: 'end',
    description: 'End with approved outcome',
    config: { outcome: 'approved' },
  },
];

// Extended actions for dropdown
const MORE_ACTIONS = [
  {
    id: 'liveness',
    label: 'Liveness',
    icon: User,
    nodeType: 'step',
    componentId: 'liveness',
    description: 'Active liveness check',
  },
  {
    id: 'address',
    label: 'Address',
    icon: MapPin,
    nodeType: 'step',
    componentId: 'address',
    description: 'Address verification',
  },
  {
    id: 'delay',
    label: 'Delay',
    icon: Clock,
    nodeType: 'step',
    componentId: 'delay',
    description: 'Wait before continuing',
  },
  {
    id: 'end_rejected',
    label: 'Reject',
    icon: X,
    nodeType: 'end',
    componentId: 'end',
    description: 'End with rejected outcome',
    config: { outcome: 'rejected' },
  },
];

export function QuickActionsBar({
  visible,
  position,
  onAddNode,
  onClose,
  selectedNode,
}) {
  const [showMore, setShowMore] = useState(false);
  const barRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        setShowMore(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddNode = useCallback((action) => {
    onAddNode({
      type: action.nodeType,
      data: {
        label: action.label,
        componentType: action.componentId,
        component_id: action.componentId,
        description: action.description,
        ...action.config,
      },
      position: position || { x: 250, y: 250 },
    });
    onClose?.();
    setShowMore(false);
  }, [onAddNode, onClose, position]);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={barRef}
        className="quick-actions-bar"
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        style={{
          position: 'absolute',
          left: position?.x || '50%',
          top: position?.y || 'auto',
          bottom: position ? 'auto' : '80px',
          transform: position ? 'translate(-50%, 0)' : 'translateX(-50%)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 12px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Quick action buttons */}
        {QUICK_ACTIONS.map((action) => {
          const Icon = action.icon;
          return (
            <motion.button
              key={action.id}
              className="quick-action-btn"
              onClick={() => handleAddNode(action)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={action.description}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={14} />
              {action.label}
            </motion.button>
          );
        })}

        {/* More actions dropdown */}
        <div style={{ position: 'relative' }}>
          <motion.button
            className="quick-action-more"
            onClick={() => setShowMore(!showMore)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 10px',
              background: showMore ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              color: showMore ? 'white' : 'var(--text-secondary)',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={14} />
            <ChevronDown
              size={12}
              style={{
                transform: showMore ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </motion.button>

          {/* Dropdown menu */}
          <AnimatePresence>
            {showMore && (
              <motion.div
                className="quick-action-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginBottom: '8px',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
                  minWidth: '160px',
                }}
              >
                {MORE_ACTIONS.map((action) => {
                  const Icon = action.icon;
                  return (
                    <motion.button
                      key={action.id}
                      onClick={() => handleAddNode(action)}
                      whileHover={{ backgroundColor: 'var(--bg-hover)' }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        color: 'var(--text-primary)',
                        fontSize: '13px',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <Icon size={14} style={{ color: 'var(--text-secondary)' }} />
                      {action.label}
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Keyboard hint */}
        <div
          style={{
            marginLeft: '8px',
            paddingLeft: '8px',
            borderLeft: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: 'var(--text-tertiary)',
            fontSize: '11px',
          }}
        >
          <Command size={10} />
          <span>K</span>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Hook for keyboard shortcuts in workflow builder
 */
export function useWorkflowKeyboardShortcuts({
  onAddNode,
  onDeleteNode,
  onDuplicateNode,
  onEditNode,
  onUndo,
  onRedo,
  onToggleQuickActions,
  selectedNode,
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Cmd/Ctrl + K - Toggle quick actions / command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onToggleQuickActions?.();
        return;
      }

      // Delete/Backspace - Delete selected node
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNode) {
        e.preventDefault();
        onDeleteNode?.(selectedNode);
        return;
      }

      // D - Duplicate selected node
      if (e.key === 'd' && !e.metaKey && !e.ctrlKey && selectedNode) {
        e.preventDefault();
        onDuplicateNode?.(selectedNode);
        return;
      }

      // E or Enter - Edit selected node
      if ((e.key === 'e' || e.key === 'Enter') && selectedNode) {
        e.preventDefault();
        onEditNode?.(selectedNode);
        return;
      }

      // N - Add new node (show quick actions)
      if (e.key === 'n' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onToggleQuickActions?.();
        return;
      }

      // Cmd/Ctrl + Z - Undo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        onUndo?.();
        return;
      }

      // Cmd/Ctrl + Shift + Z - Redo
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        onRedo?.();
        return;
      }

      // Cmd/Ctrl + Y - Redo (alternative)
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        onRedo?.();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    onAddNode,
    onDeleteNode,
    onDuplicateNode,
    onEditNode,
    onUndo,
    onRedo,
    onToggleQuickActions,
    selectedNode,
  ]);
}

export default QuickActionsBar;
